import { sessionStorageService } from '@access-erp/common';
import { 
  UserConfigurationAdvancedFilter, 
  UserConfigurationColumn, 
  UserConfigurationContext, 
  UserConfigurationGroup, 
  UserConfigurationState 
} from '@access-erp/ui-components';
import { JSX, PropsWithChildren, useCallback, useMemo, useState } from 'react';

import jwtDecode from 'jwt-decode';

import { ApiUserConfiguration, useUserConfigurationData } from '../hooks/useUserConfigurationData';
import { getSubscriptionId } from '@/utils/auth/subscription.utils';
import { getAccessToken } from '@/utils/auth/token.utils';

export const convertFromServerModel = (
  apiConfiguration: ApiUserConfiguration,
  formName: string,
): UserConfigurationState => {
  const version = +!!apiConfiguration;
  return {
    advancedFilters: (apiConfiguration?.advancedFilters ?? []) as UserConfigurationAdvancedFilter[],
    grouping: (apiConfiguration?.grouping ?? []) as UserConfigurationGroup[],
    headerColumns: (apiConfiguration?.columns?.filter((c) => !c?.isDetailColumn) ?? []) as UserConfigurationColumn[],
    headerColumnsVersion: version,
    detailColumns: (apiConfiguration?.columns?.filter((c) => c?.isDetailColumn) ?? []) as UserConfigurationColumn[],
    detailColumnsVersion: version,
    hasChanged: false,
    //set form in case we don't get it from the server
    form: formName,
    loading: false,
  };
};

export const convertToServerModel = ({
  advancedFilters,
  advancedFiltersPinned,
  detailColumns,
  form,
  grouping,
  headerColumns,
}: UserConfigurationState): ApiUserConfiguration => ({
  advancedFilters: advancedFilters,
  advancedFiltersPinned,
  columns: [...(headerColumns ?? []), ...(detailColumns ?? [])],
  grouping: grouping,
  form,
});

export const ConfigurationProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const [localConfiguration, setLocalConfiguration] = useState<Record<string, UserConfigurationState | null>>({});

  const apiHooks = useUserConfigurationData(localConfiguration);

  const getSessionStorageHandlers = (instId: string, formName: string) =>
    sessionStorageService<UserConfigurationState>(`${instId}-UserConfiguration-${formName}`);

  const getUserName = (identityToken: string): string => jwtDecode<{ name: string }>(identityToken).name;

  const getConfiguration = useCallback(
    async (formName: string): Promise<UserConfigurationState> => {
      const subscriptionId = await getSubscriptionId();
      const identityToken = await getAccessToken();
      const userName = getUserName(identityToken);
      
      if (localConfiguration[formName]) {
        return localConfiguration[formName]!;
      } else {
        const sessionValue = getSessionStorageHandlers(subscriptionId!, formName).get();

        if (sessionValue) {
          setLocalConfiguration((prev) => ({
            ...prev,
            [formName]: sessionValue,
          }));
          return sessionValue;
        } else {
          const apiConfiguration = await apiHooks.getConfiguration(userName, formName);
          const configuration = convertFromServerModel(apiConfiguration, formName);

          getSessionStorageHandlers(subscriptionId!, formName).set(configuration);
          setLocalConfiguration((prev) => ({
            ...prev,
            [formName]: configuration,
          }));

          return configuration;
        }
      }
    },
    [localConfiguration, apiHooks],
  );

  const saveConfiguration = useCallback(
    async (newConfiguration: UserConfigurationState) => {
      const subscriptionId = await getSubscriptionId();
      const identityToken = await getAccessToken();
      const userName = getUserName(identityToken);
      const formName = newConfiguration?.form;

      if (formName) {
        getSessionStorageHandlers(subscriptionId!, formName).set(newConfiguration);
        setLocalConfiguration((prev) => ({
          ...prev,
          [formName]: newConfiguration,
        }));

        const serverModel = convertToServerModel(newConfiguration);
        return apiHooks.saveConfiguration(userName, serverModel);
      } else {
        return false;
      }
    },
    [apiHooks],
  );

  const deleteConfiguration = useCallback(
    async (formName: string) => {
      if (formName) {
        const subscriptionId = await getSubscriptionId();
        const identityToken = await getAccessToken();
        const userName = getUserName(identityToken);
        
        getSessionStorageHandlers(subscriptionId!, formName).remove();
        setLocalConfiguration((prev) => ({ ...prev, [formName]: null }));

        return apiHooks.deleteConfiguration(userName, formName);
      } else {
        return false;
      }
    },
    [apiHooks],
  );

  const value = useMemo(
    () => ({
      getConfiguration,
      saveConfiguration,
      deleteConfiguration,
    }),
    [getConfiguration, saveConfiguration, deleteConfiguration],
  );

  return (
    <UserConfigurationContext.Provider value={value}>{children}</UserConfigurationContext.Provider>
  );
};
